import React, { useReducer, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal, notification } from "antd";
import i18n from "i18next";
import * as R from "ramda";
import { useTranslation } from "react-i18next";

import api from "../../../../utils/api";
import { DEFAULT_PROCESS_STEPS } from "../../../../constants/opportunity";
import { idInList } from "../../../../utils/candidates";
import { selectCandidate } from "../../../../store/actions/process";

import {
  parseSteps,
  parseMatches,
  normalizeCandidatesForRequestedStep,
  getCandidatesForStep,
  getStepsAfterMove,
  addUnreadMessages
} from "./selectors";
import ProcessSkeleton from "./ProcessSkeleton";
import FullPosition from "./FullPosition";
import EmptySteps from "./EmptySteps";

const INITIAL_STATE = {
  steps: {}, // List of available steps
  matches: {}, // List of matches
  stepPagination: {}, // Step-level pagination state for opt-in match loading
  stepsOrder: [], // Order of the steps
  targets: [], // Steps where i can drop the card i'm dragging
  status: "booting", // Status,
  draggingStep: null // Dragging match id
};

const MATCHES_PAGE_LIMIT = 20;

// Maps backend TuRecibo error codes to localized toast messages.
const TU_RECIBO_ERROR_TO_TOAST = {
  turecibo_username_exists: "toasts:candidates.tuReciboUsernameExists",
  turecibo_email_exists: "toasts:candidates.tuReciboEmailExists",
  turecibo_location_required: "toasts:candidates.tuReciboLocationRequired",
  turecibo_sector_required: "toasts:candidates.tuReciboSectorRequired",
  turecibo_invalid_labor_id: "toasts:candidates.tuReciboInvalidLaborId"
};

// Supports axios errors and normalized backend error objects.
function getUpdateMatchStepErrorMessage(error) {
  if (error && error.integration === "tuRecibo") return error;

  return (
    R.path(["response", "data", "message"], error) ||
    R.path(["data", "message"], error) ||
    R.path(["message", "message"], error) ||
    R.prop("message", error)
  );
}

// Returns a specific TuRecibo error toast when available, otherwise the generic fallback.
function getUpdateMatchStepErrorDescription(error) {
  const message = getUpdateMatchStepErrorMessage(error);
  const tuReciboErrorCode =
    message && message.integration === "tuRecibo" ? message.code : undefined;
  const translationKey = TU_RECIBO_ERROR_TO_TOAST[tuReciboErrorCode];

  return translationKey
    ? i18n.t(translationKey)
    : i18n.t("toasts:candidates.updateMatchStepRejected");
}

function showTuReciboNotification(result) {
  const tuReciboNotification = result && result.tuReciboNotification;

  if (!tuReciboNotification || !tuReciboNotification.message) return;

  notification.warning({
    message: i18n.t("global:alert"),
    description: tuReciboNotification.message
  });
}

function noop() {}

// This will let us know the state types and autocomplete in VSCode :-)
const ProcessContext = React.createContext({
  state: INITIAL_STATE,
  actions: {
    sendOffer: noop,
    updateStep: noop,
    startDrag: noop,
    endDrag: noop,
    loadStepMatches: noop,
    removeMatch: noop,
    moveMatch: noop
  },
  getContext() {
    return {
      dispatch: noop,
      state: {},
      updateStepColumnOrder: noop
    };
  }
});

function emailInviteModal({ onOk, ...modalConfig }) {
  return new Promise((resolve, reject) => {
    const modal = Modal.confirm({
      ...modalConfig,
      okButtonProps: {
        ...(modalConfig.okButtonProps || {}),
        "data-test": "email-invite-modal-button-ok"
      },
      cancelButtonProps: {
        ...(modalConfig.cancelButtonProps || {}),
        "data-test": "email-invite-modal-button-cancel"
      },
      autoFocusButton: "cancel",
      onCancel: undefined,
      onOk: undefined
    });
    modal.update({
      onCancel: () => resolve(), // TODO Temporary fix, we should remove the modal in the confirmation step.
      onOk: async () => {
        modal.update({ okButtonProps: { loading: true } });
        try {
          await onOk();
          resolve();
        } catch (error) {
          reject(error);
        }
      }
    });
  });
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_MATCHES_LOADING": {
      const stepMatchesUpdate = R.map(
        R.when(
          idInList(R.map(R.prop("id"), action.payload.matches)),
          action.payload.loading
            ? R.assoc("loading", true)
            : R.dissoc("loading")
        )
      );

      return R.evolve(
        {
          steps: {
            [action.payload.step.id]: {
              matches: stepMatchesUpdate
            }
          }
        },
        state
      );
    }

    case "SET_MATCHES_INVITE_LOADING": {
      const { stepId, matches, loading } = action.payload;
      const matchesIds = R.map(R.prop("id"), matches);
      const update = R.map(
        R.when(idInList(matchesIds), R.assoc("inviteCandidateStatus", loading))
      );

      return !loading || loading === "rejected"
        ? state
        : R.evolve(
            {
              matches: update,
              steps: {
                [stepId]: {
                  matches: update
                }
              }
            },
            state
          );
    }

    case "RESET": {
      return { ...INITIAL_STATE };
    }

    // payload: { stepId: string, step: Step }
    case "UPDATE_STEP": {
      const { stepId, step } = action.payload;
      return R.evolve(
        {
          steps: R.map(R.when(R.propEq("id", stepId), R.mergeDeepLeft(step)))
        },
        state
      );
    }
    // payload: { steps: arrayOf<Step> }
    case "UPDATE_STEPS": {
      const { steps } = action.payload;
      return {
        ...state,
        steps
      };
    }
    // payload: {steps: Array<step>, matches: Array<match>, stepPagination?: Object}
    case "BOOT_RESOLVED": {
      const matches = parseMatches(action.payload.matches);
      const steps = parseSteps(action.payload.steps, matches);

      const stepsOrder = R.pipe(
        R.sortBy(R.prop("order")),
        R.pluck("id")
      )(action.payload.steps);

      return {
        ...state,
        steps,
        matches,
        stepPagination: action.payload.stepPagination || {},
        stepsOrder,
        targets: stepsOrder,
        status: "init"
      };
    }

    case "LOAD_STEP_MATCHES_STARTED": {
      const { stepId } = action.payload;

      return R.assocPath(["stepPagination", stepId, "loading"], true, state);
    }

    case "LOAD_STEP_MATCHES_RESOLVED": {
      const { stepId, matches, pagination } = action.payload;
      const stepMatches = getCandidatesForStep(stepId, matches);
      const matchesById = parseMatches(stepMatches);
      const previousPagination = R.pathOr(
        {},
        ["stepPagination", stepId],
        state
      );
      const stepColumnOrder = R.pathOr(
        [],
        ["steps", stepId, "columnOrder"],
        state
      );
      const nextStepMatches = R.pipe(
        R.concat(R.pathOr([], ["steps", stepId, "matches"], state)),
        R.uniqBy(R.prop("id")),
        R.sortBy(match => {
          const index = R.findIndex(R.equals(match.id), stepColumnOrder);
          return index === -1 ? Number.MAX_SAFE_INTEGER : index;
        })
      )(stepMatches);
      const loadedCount = R.length(nextStepMatches);
      const safeTotal = Math.max(
        R.propOr(0, "total", previousPagination),
        R.propOr(0, "total", pagination || {}),
        loadedCount,
        R.length(stepColumnOrder)
      );
      const hasNext =
        R.propOr(false, "hasNext", pagination || {}) || loadedCount < safeTotal;

      return R.pipe(
        R.assoc("matches", R.mergeRight(state.matches, matchesById)),
        R.assocPath(["steps", stepId, "matches"], nextStepMatches),
        R.assocPath(["stepPagination", stepId], {
          ...pagination,
          total: safeTotal,
          hasNext,
          loading: false
        })
      )(state);
    }

    case "LOAD_STEP_MATCHES_REJECTED": {
      const { stepId } = action.payload;

      return R.assocPath(["stepPagination", stepId, "loading"], false, state);
    }
    // payload: string
    case "BOOT_REJECTED": {
      return { ...state, status: "crashed" };
    }
    // payload: Array<Step>
    case "UPDATE_STEPS_STARTED": {
      return R.pipe(
        R.assoc("steps", action.payload),
        R.assoc("status", "pending")
      )(state);
    }
    // payload: Array<Step>
    case "UPDATE_STEPS_REJECTED": {
      notification.error({
        message: i18n.t("global:error"),
        description: action.payload.inviteFailed
          ? i18n.t("toasts:candidates.inviteFailed")
          : getUpdateMatchStepErrorDescription(action.payload.error)
      });

      return R.pipe(
        R.assoc("steps", action.payload.steps),
        R.assoc("status", "rejected")
      )(state);
    }
    // payload: undefined
    case "UPDATE_STEPS_RESOLVED": {
      return R.assoc("status", "resolved")(state);
    }
    // payload: undefined
    case "REMOVE_MATCH_REJECTED": {
      notification.error({
        message: i18n.t("global:error"),
        description: i18n.t("toasts:candidates.removeMatchRejected")
      });

      return R.pipe(
        R.assoc("steps", action.payload),
        R.assoc("status", "rejected")
      )(state);
    }
    // payload: Array<Step>
    case "MOVE_MATCH_TO_STEP": {
      return R.assoc("steps", action.payload)(state);
    }
    // payload: {source: ID, match: ID}
    case "DRAG_START": {
      const stepIndex = R.findIndex(
        R.equals(action.payload.source),
        state.stepsOrder
      );

      return {
        ...state,
        targets: R.slice(stepIndex, state.stepsOrder.length, state.stepsOrder),
        draggingStep: action.payload.source,
        draggingMatch: action.payload.match
      };
    }
    // payload: {}
    case "DRAG_END": {
      return {
        ...state,
        draggingStep: null,
        draggingMatch: null,
        targets: state.stepsOrder
      };
    }

    case "SEND_OFFER": {
      notification.success({
        message: i18n.t("global:good"),
        description: i18n.t("toasts:sendOffer.success")
      });

      // The payload is the document the server stored, and its `status` is the truth. Writing
      // 'sent' here would show a settled offer as sent and hide the re-send button, because
      // ProcessCard only offers it when there is no offer or the last one is 'rejected'.
      return R.evolve({
        steps: {
          [action.payload.idStep]: {
            matches: R.map(
              R.when(
                R.propEq("id", action.payload.idCandidate),
                R.assoc("offerletter", [action.payload])
              )
            )
          }
        },
        matches: R.map(
          R.when(
            R.propEq("id", action.payload.idCandidate),
            R.assoc("offerletter", [action.payload])
          )
        )
      })(state);
    }
    case "SEND_OFFER_REJECTED": {
      notification.error({
        message: i18n.t("global:error"),
        description: i18n.t("toasts:sendOffer.error")
      });

      return state;
    }
    default:
      return state;
  }
}

const ProcessProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { t } = useTranslation(["global", "opportunity", "toasts"]);
  const [positionIsFull, setPositionIsFull] = React.useState(false);
  const {
    company,
    opportunity,
    channels,
    selectedCandidatesStep
  } = useSelector(
    R.applySpec({
      company: R.prop("company"),
      opportunity: R.path(["opportunity", "selected"]),
      channels: R.path(["messages", "channels"]),
      selectedCandidatesStep: R.path(["process", "selectedCandidatesStep"])
    })
  );

  const reduxDispatch = useDispatch();
  const deselectCandidate = useCallback(
    candidate =>
      reduxDispatch(
        selectCandidate({
          candidate,
          selected: false,
          step: selectedCandidatesStep
        })
      ),
    [reduxDispatch, selectedCandidatesStep]
  );

  function getProcess(opportunityId, companyId) {
    return api.opportunity.process.fetch(companyId, opportunityId).then(steps =>
      Promise.all([
        Promise.all(
          steps.map(async step => {
            if (!R.propEq("type", "group-interview", step))
              return R.assoc("rooms", [], step);

            const rooms = await api.opportunity.process.step.room.list(
              companyId,
              opportunityId,
              step.id
            );

            return R.assoc("rooms", rooms, step);
          })
        ),
        Promise.all(
          steps.map(step =>
            api.opportunity.matches
              .fetch(companyId, opportunityId, {
                page: 1,
                limit: MATCHES_PAGE_LIMIT,
                stepId: step.id
              })
              .then(({ candidates, pagination }) => ({
                stepId: step.id,
                candidates: normalizeCandidatesForRequestedStep(
                  step.id,
                  candidates
                ),
                pagination
              }))
              .catch(() => ({
                stepId: step.id,
                candidates: [],
                pagination: {
                  page: 1,
                  limit: MATCHES_PAGE_LIMIT,
                  total: R.length(R.propOr([], "columnOrder", step)),
                  loading: false,
                  error: true
                }
              }))
          )
        )
      ]).then(([stepsWithRooms, matchesByStep]) => [
        stepsWithRooms,
        R.chain(R.prop("candidates"), matchesByStep),
        R.pipe(
          R.map(({ stepId, pagination }) => [
            stepId,
            { ...pagination, loading: false }
          ]),
          R.fromPairs
        )(matchesByStep)
      ])
    );
  }

  function updateStepColumnOrder(step) {
    const loadedColumnOrder = R.pipe(
      R.prop("matches"),
      R.pluck("id")
    )(step);
    const previousLoadedColumnOrder = R.pipe(
      R.pathOr([], ["steps", step.id, "matches"]),
      R.pluck("id")
    )(state);
    const previousColumnOrder = R.pathOr(
      [],
      ["steps", step.id, "columnOrder"],
      state
    );
    const hasPartialMatches = Boolean(
      R.path(["stepPagination", step.id], state)
    );
    const columnOrder = hasPartialMatches
      ? R.concat(
          loadedColumnOrder,
          R.reject(
            id => R.includes(id, previousLoadedColumnOrder),
            previousColumnOrder
          )
        )
      : loadedColumnOrder;

    return api.opportunity.process.step
      .update(company.id, opportunity.id, step.id, {
        columnOrder
      })
      .catch(e => {
        console.error(
          "Hubo un error actualizando el orden de las tarjetas: " + e.message
        );
        throw e;
      });
  }

  function updateMatchStep(matchId, stepId) {
    return api.opportunity.matches.step
      .update(company.id, opportunity.id, matchId, stepId)
      .catch(e => {
        console.error(
          "Hubo un error actualizando el orden de las tarjetas: " + e.message
        );
        throw e;
      });
  }

  function inviteCandidate(matchId) {
    return api.opportunity.candidates
      .inviteExternalCandidate(company.id, opportunity.id, matchId)
      .then(() =>
        notification.success({
          message: i18n.t("global:good"),
          description: i18n.t("toasts:opportunity.sendEmailExternalResolved")
        })
      )
      .catch(e => {
        notification.error({
          message: i18n.t("global:error"),
          description: i18n.t("toasts:opportunity.sendEmailExternalRejected")
        });
        console.error("Hubo un error al enviar la invitación: " + e.message);
        // TODO revisar.
        throw new Error(e);
      });
  }

  async function moveCard({
    matchId,
    sourceStep,
    destinationStep,
    sourceMatchIndex = 0,
    destinationMatchIndex = 0
  }) {
    const stepChanged = destinationStep !== sourceStep;
    const orderChanged = destinationMatchIndex !== sourceMatchIndex;

    const candidate = R.path(["matches", matchId], state);

    // TODO change pathOr to path
    const isRegistered = R.propOr(true, "isRegistered", candidate);

    const fullname = R.propOr(
      // TODO check fallback y mandar a i18n
      t("opportunity:process.candidateFallback"),
      "fullname",
      candidate
    );

    if (
      stepChanged &&
      R.equals(sourceStep, R.prop("id", selectedCandidatesStep))
    )
      deselectCandidate(state.matches[matchId]);

    // Si hay destination es que se solto la card en alguna columna, si los draggableId e indexes son iguales se solto en el mismo lugar
    // Si no está registrado, es necesario enviarle una invitación.
    try {
      if (stepChanged || orderChanged) {
        // if (stepChanged || orderChanged) {
        // Obtenemos los nuevos steps con el orden y cards respectivos despues de hacer el move
        const steps = getStepsAfterMove(
          { sourceStep, destinationStep, destinationMatchIndex, matchId },
          state
        );

        const lastStep = R.find(
          R.propEq("name", DEFAULT_PROCESS_STEPS.accepted)
        )(R.values(steps));

        if (
          lastStep.matches.length >= opportunity.positions &&
          destinationStep === lastStep.id
        ) {
          setPositionIsFull(true);
        }

        // Iniciamos el loading y seteamos los nuevos steps al state
        dispatch({
          type: "UPDATE_STEPS_STARTED",
          payload: steps
        });

        try {
          // Actualizamos el orden de las cards dentro del step, al no ser tan importante hacemos un fire and forget
          updateStepColumnOrder(steps[sourceStep]);

          if (stepChanged) {
            // Si la columna destino no es la misma que la de origen la actualizamos tambien
            updateStepColumnOrder(steps[destinationStep]);

            if (!isRegistered)
              await emailInviteModal({
                onOk: () => inviteCandidate(matchId),
                title: t("global:alert"),
                content: (
                  <>
                    <p>
                      {t("opportunity:process.externalCandidateModalBody", {
                        fullname
                      })}
                    </p>
                    <b>
                      {t("opportunity:process.externalCandidateModalQuestion")}
                    </b>
                  </>
                ),
                okText: t("global:invite"),
                cancelText: t("opportunity:process.skipEmailInvitation")
              });

            // Actualizamos el step en la card que movimos
            console.log("request unsent");
            const updateMatchStepResult = await updateMatchStep(
              matchId,
              destinationStep
            );
            showTuReciboNotification(updateMatchStepResult);
            console.log("request sent");

            // Sacamos el loading cuando resuelve
            dispatch({ type: "UPDATE_STEPS_RESOLVED" });
          } else {
            // En caso de no tener que actualizar el match reiniciamos el status
            dispatch({ type: "UPDATE_STEPS_RESOLVED" });
          }
        } catch (e) {
          console.log("e", e);
          // Restauramos los steps anteriores en caso de que falle
          dispatch({
            type: "UPDATE_STEPS_REJECTED",
            payload: { steps: state.steps, inviteFailed: false, error: e }
          });
        }
      }
    } catch (error) {
      console.log("error", error);
      dispatch({
        type: "UPDATE_STEPS_REJECTED",
        payload: { steps: state.steps, inviteFailed: true }
      });
    }
  }

  function updateStep(stepId, step) {
    dispatch({
      type: "UPDATE_STEP",
      payload: {
        stepId,
        step
      }
    });
  }

  function handleDragStart(result) {
    dispatch({
      type: "DRAG_START",
      payload: {
        source: result.source.droppableId,
        match: result.draggableId
      }
    });
  }

  async function handleDragEnd(result) {
    // Extraemos todos los datos que necesitamos del drag
    const { source, destination, draggableId: matchId } = result;
    const { droppableId: sourceStep, index: sourceMatchIndex } = source;

    // Terminamos el drag
    dispatch({ type: "DRAG_END" });

    // Solo movemos la card si se droppeo en un lugar habilitado
    if (destination) {
      const {
        droppableId: destinationStep,
        index: destinationMatchIndex
      } = destination;

      // Movemos la card al step correspondiente
      moveCard({
        matchId,
        sourceStep,
        sourceMatchIndex,
        destinationStep,
        destinationMatchIndex
      });
    }
  }

  function handleMoveMatch(match, destinationStep) {
    // TODO revise this. It fails if match.step is undefined
    moveCard({
      destinationStep,
      matchId: match.id,
      sourceStep: match.step
    });
  }

  async function handleRemoveMatch(stepId, matchId) {
    // TODO change propOr to prop
    const isRegistered = R.pathOr(
      true,
      ["matches", matchId, "isRegistered"],
      state
    );

    const updatedSteps = R.evolve({
      [stepId]: {
        matches: R.reject(R.propEq("id", matchId))
      }
    })(state.steps);

    dispatch({ type: "UPDATE_STEPS_STARTED", payload: updatedSteps });

    if (R.equals(stepId, R.prop("id", selectedCandidatesStep)))
      deselectCandidate(state.matches[matchId]);

    try {
      if (isRegistered) {
        api.messages.read(opportunity.id, matchId, company.id);

        await api.opportunity.candidates.unmatch(
          company.id,
          opportunity.id,
          matchId
        );
      } else {
        await api.opportunity.candidates.externalDislike(
          company.id,
          opportunity.id,
          matchId
        );
      }
      notification.success({
        message: t("global:good"),
        description: t("toasts:candidates.rejectedCandidate", {
          fullname: R.or(
            R.path(["matches", matchId, "fullname"], state),
            `${R.path(["matches", matchId, "first"], state)} ${R.path(
              ["matches", matchId, "last"],
              state
            )}`
          )
        })
      });

      await updateStepColumnOrder(updatedSteps[stepId]);

      dispatch({ type: "UPDATE_STEPS_RESOLVED" });
    } catch (e) {
      dispatch({
        type: "REMOVE_MATCH_REJECTED",
        payload: state.steps
      });
    }
  }

  async function sendOffer(payload) {
    try {
      const data = await api.opportunity.candidates.sendOffer(
        opportunity.id,
        payload.idCandidate,
        company.id,
        { ...payload.formData }
      );

      dispatch({
        type: "SEND_OFFER",
        payload: R.assoc("idStep", payload.idStep, data)
      });
    } catch (e) {
      dispatch({
        type: "SEND_OFFER_REJECTED"
      });
    }
  }

  function setMatchesInviteLoading(stepId, matches, loading) {
    return {
      type: "SET_MATCHES_INVITE_LOADING",
      payload: {
        stepId,
        matches,
        loading
      }
    };
  }

  async function refreshProcess() {
    dispatch({
      type: "RESET"
    });
    getProcess(opportunity.id, company.id)
      .then(([steps, matches, stepPagination]) => {
        dispatch({
          type: "BOOT_RESOLVED",
          payload: {
            steps,
            matches,
            stepPagination
          }
        });
      })
      .catch(e => {
        dispatch({ type: "BOOT_REJECTED", payload: e.message });
      });
  }

  async function loadStepMatches(stepId, page = 1, limit = MATCHES_PAGE_LIMIT) {
    dispatch({ type: "LOAD_STEP_MATCHES_STARTED", payload: { stepId } });

    try {
      const { candidates, pagination } = await api.opportunity.matches.fetch(
        company.id,
        opportunity.id,
        { page, limit, stepId }
      );

      dispatch({
        type: "LOAD_STEP_MATCHES_RESOLVED",
        payload: {
          stepId,
          matches: candidates,
          pagination
        }
      });
    } catch (e) {
      dispatch({ type: "LOAD_STEP_MATCHES_REJECTED", payload: { stepId } });
      throw e;
    }
  }

  React.useEffect(() => {
    getProcess(opportunity.id, company.id)
      .then(([steps, matches, stepPagination]) => {
        dispatch({
          type: "BOOT_RESOLVED",
          payload: {
            steps,
            matches,
            stepPagination
          }
        });
      })
      .catch(e => {
        dispatch({ type: "BOOT_REJECTED", payload: e.message });
      });
  }, [opportunity.id, company.id, dispatch]);

  if (R.propEq("status", "booting", state)) return <ProcessSkeleton />;
  if (R.propSatisfies(R.isEmpty, "steps", state)) return <EmptySteps />;

  // TODO move exerything that we can to Redux
  // Esta función la usamos en conjunto con Redux en todas las acciones de selección múltiple, es la forma de que Redux modifique este contexto.
  function getContext() {
    return {
      dispatch,
      state,
      updateStepColumnOrder,
      setMatchesInviteLoading,
      setPositionIsFull
    };
  }

  return (
    <ProcessContext.Provider
      value={{
        state: R.pipe(
          R.mergeLeft({ positionIsFull, opportunity }),
          state => addUnreadMessages(opportunity, channels, state)
        )(state),
        actions: {
          sendOffer,
          loadStepMatches,
          updateStep,
          refreshProcess,
          startDrag: handleDragStart,
          endDrag: handleDragEnd,
          removeMatch: handleRemoveMatch,
          moveMatch: handleMoveMatch
        },
        getContext
      }}
    >
      {positionIsFull && (
        <FullPosition onClose={() => setPositionIsFull(false)} />
      )}
      {children}
    </ProcessContext.Provider>
  );
};

export { ProcessContext as default, ProcessProvider as Provider };
